import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import MuiTooltip from "@mui/material/Tooltip";
import { useEffect, useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const STAGE_COLORS = [
    "#004b6e",
    "#2bbcc0",
    "#2f6f8f",
    "#5b8fa9",
    "#7da6b6",
    "#a5bdc9",
    "#3e4c59",
    "#587b7f",
    "#8c7264",
    "#6c7a89",
    "#95a5a6",
    "#b8c2cc",
];

const formatDollar = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${Math.round(value / 1000)}K`;
    return `$${Math.round(value)}`;
};

const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 13,
            }}
        >
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} style={{ color: p.fill, margin: "2px 0" }}>
                    {p.dataKey}: {p.value} deal{p.value !== 1 ? "s" : ""}
                </p>
            ))}
            <p
                style={{
                    fontWeight: 600,
                    marginTop: 4,
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: 4,
                }}
            >
                Total: {total} deal{total !== 1 ? "s" : ""}
            </p>
        </div>
    );
};

const DollarBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 13,
            }}
        >
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} style={{ color: p.fill, margin: "2px 0" }}>
                    {p.name}: {formatDollar(p.value)}
                </p>
            ))}
            <p
                style={{
                    fontWeight: 600,
                    marginTop: 4,
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: 4,
                }}
            >
                Total: {formatDollar(total)}
            </p>
        </div>
    );
};

const LineTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 13,
            }}
        >
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
            <p style={{ color: "#2bbcc0", margin: "2px 0" }}>
                Weighted Forecast: {formatDollar(payload[0].value)}
            </p>
            <p style={{ color: "#999", fontSize: 11, margin: "4px 0 0" }}>
                Sum of (deal amount × close probability)
            </p>
        </div>
    );
};

const sectionStyle = {
    padding: "16px 0 8px",
};

const headerStyle = {
    fontSize: "1.1rem",
    fontWeight: 700,
    marginBottom: 2,
    paddingLeft: 4,
};

const subtitleStyle = {
    fontSize: "0.8rem",
    color: "#888",
    paddingLeft: 4,
    marginBottom: 12,
};

const chartTitleStyle = {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: 8,
    paddingLeft: 4,
};

const PipelineCharts = ({
    fetchData,
    selectedProducts,
    selectedProductFamily,
    selectedBusinessUnit,
}) => {
    const [allDeals, setAllDeals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        fetchData("getpipelinedata")
            .then((data) => setAllDeals(data?.deals || []))
            .catch((err) => console.error("Error fetching pipeline data:", err))
            .finally(() => setIsLoading(false));
    }, [fetchData]);

    const { monthlyData, stages } = useMemo(() => {
        const filtered = allDeals.filter((deal) => {
            if (!deal.closedate) return false;
            // ponytail: case-insensitive match in case HubSpot name has different casing
            if (deal.pipelineName?.toLowerCase() !== "revenue pipeline") return false;

            if (selectedProducts && selectedProducts.length > 0) {
                const selectedIds = new Set(selectedProducts.map((p) => String(p.id)));
                return deal.products.some(
                    (p) => p.netsuite_product_id && selectedIds.has(String(p.netsuite_product_id))
                );
            }
            if (selectedProductFamily) {
                return deal.products.some(
                    (p) => String(p.product_family_id) === String(selectedProductFamily.id)
                );
            }
            if (selectedBusinessUnit) {
                return deal.products.some(
                    (p) => String(p.business_unit_id) === String(selectedBusinessUnit.id)
                );
            }
            return true;
        });

        const monthMap = {};
        filtered.forEach((deal) => {
            // ponytail: parse date string directly to avoid UTC→local timezone shift
            const [year, month] = deal.closedate.split("T")[0].split("-");
            const monthKey = `${year}-${month}`;
            const monthLabel = new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleString(
                "en-US",
                { month: "short", year: "numeric" }
            );
            const stage = deal.stageName || "Unknown";

            if (!monthMap[monthKey]) {
                monthMap[monthKey] = { monthKey, month: monthLabel, weightedTotal: 0 };
            }
            monthMap[monthKey][stage] = (monthMap[monthKey][stage] || 0) + 1;
            monthMap[monthKey][`${stage}__amt`] =
                (monthMap[monthKey][`${stage}__amt`] || 0) + (deal.amount || 0);
            monthMap[monthKey].weightedTotal += (deal.amount || 0) * (deal.probability ?? 0);
        });

        const sorted = Object.values(monthMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
        const allStages = [...new Set(filtered.map((d) => d.stageName || "Unknown"))].sort();

        return { monthlyData: sorted, stages: allStages };
    }, [allDeals, selectedProducts, selectedProductFamily, selectedBusinessUnit]);

    const isFiltered =
        selectedProducts?.length > 0 || selectedProductFamily || selectedBusinessUnit;

    const sectionHeader = (
        <div style={{ marginBottom: 24 }}>
            <h2 style={headerStyle}>Sales Pipeline</h2>
            <p style={subtitleStyle}>Revenue Pipeline · Open deals only</p>
        </div>
    );

    if (isLoading) {
        return (
            <div style={sectionStyle}>
                {sectionHeader}
                <p style={{ color: "#888", fontSize: 14, paddingLeft: 4 }}>
                    Loading pipeline data...
                </p>
            </div>
        );
    }

    if (!monthlyData.length) {
        return (
            <div style={sectionStyle}>
                {sectionHeader}
                <p style={{ color: "#888", fontSize: 14, paddingLeft: 4 }}>
                    {isFiltered
                        ? "No open deals found for the selected filters."
                        : "No open pipeline deals found."}
                </p>
            </div>
        );
    }

    return (
        <div style={sectionStyle}>
            {sectionHeader}
            <h3 style={{ ...chartTitleStyle, display: "flex", alignItems: "center", gap: 4 }}>
                Pipeline by Expected Close Month
                <MuiTooltip
                    title="Count of open Revenue Pipeline deals grouped by expected close month, stacked by deal stage."
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: "rgba(97,97,97,1)",
                                fontSize: "0.875rem",
                                maxWidth: 340,
                                "& .MuiTooltip-arrow": { color: "rgba(97,97,97,1)" },
                            },
                        },
                    }}
                >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: "#888" }} />
                    </IconButton>
                </MuiTooltip>
            </h3>
            <ResponsiveContainer width="100%" height={340} style={{ marginBottom: 32 }}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip content={<BarTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {stages.map((stage, i) => (
                        <Bar
                            key={stage}
                            dataKey={stage}
                            stackId="a"
                            fill={STAGE_COLORS[i % STAGE_COLORS.length]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>

            <h3
                style={{
                    ...chartTitleStyle,
                    marginTop: 32,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                Pipeline Value by Expected Close Month
                <MuiTooltip
                    title="Total deal amount of open Revenue Pipeline deals grouped by expected close month, stacked by deal stage."
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: "rgba(97,97,97,1)",
                                fontSize: "0.875rem",
                                maxWidth: 340,
                                "& .MuiTooltip-arrow": { color: "rgba(97,97,97,1)" },
                            },
                        },
                    }}
                >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: "#888" }} />
                    </IconButton>
                </MuiTooltip>
            </h3>
            <ResponsiveContainer width="100%" height={340} style={{ marginBottom: 32 }}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatDollar} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip content={<DollarBarTooltip />} />
                    <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        formatter={(value) => value.replace("__amt", "")}
                    />
                    {stages.map((stage, i) => (
                        <Bar
                            key={stage}
                            dataKey={`${stage}__amt`}
                            name={stage}
                            stackId="b"
                            fill={STAGE_COLORS[i % STAGE_COLORS.length]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>

            <h3
                style={{
                    ...chartTitleStyle,
                    marginTop: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                Weighted Forecast by Month
                <MuiTooltip
                    title="Each deal's amount multiplied by its stage close probability, summed by month. Reflects the sales team's probability-adjusted forecast."
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: "rgba(97,97,97,1)",
                                fontSize: "0.875rem",
                                maxWidth: 340,
                                "& .MuiTooltip-arrow": { color: "rgba(97,97,97,1)" },
                            },
                        },
                    }}
                >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: "#888" }} />
                    </IconButton>
                </MuiTooltip>
            </h3>
            <p style={{ ...subtitleStyle, marginBottom: 8 }}>
                Deal amount × close probability, summed by month
            </p>
            <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis
                        tickFormatter={formatDollar}
                        tick={{ fontSize: 12 }}
                        width={70}
                        label={{
                            value: "Weighted ($)",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: { fontSize: 11, fill: "#999" },
                        }}
                    />
                    <Tooltip content={<LineTooltip />} />
                    <Line
                        type="monotone"
                        dataKey="weightedTotal"
                        name="Weighted Forecast"
                        stroke="#2bbcc0"
                        strokeWidth={2}
                        dot={{ r: 4, fill: "#2bbcc0" }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PipelineCharts;
